import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Reveal from "@/components/places/Reveal";
import { useLangKV } from "@/lib/useLangKV";
import CountUp from "@/components/places/CountUp";
import AnimatedBlob from "@/components/places/AnimatedBlob";
import SEO from "@/components/places/SEO";
import VideoEmbed from "@/components/places/VideoEmbed";
import BrazilRadarMap from "@/components/places/BrazilRadarMap";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import { Play, ExternalLink, Search, Linkedin, MapPin, Compass, Lightbulb, Target, Rocket, Award, ChevronDown } from "lucide-react";

const SAFE_HTML_OPTS = {
  ALLOWED_TAGS: ["em", "strong", "span", "i", "b", "br"],
  ALLOWED_ATTR: ["class", "style"],
};
const sanitize = (html) => DOMPurify.sanitize(html || "", SAFE_HTML_OPTS);

function isoToFlag(iso2) {
  if (!iso2 || iso2.length !== 2) return "🏳";
  const A = 0x1F1E6;
  return String.fromCodePoint(A + iso2.toUpperCase().charCodeAt(0) - 65, A + iso2.toUpperCase().charCodeAt(1) - 65);
}

export default function Sobre() {
  // Auto-compute Brazil map state counts from HISTORY (city format: "Cidade/UF")
  const { pillars: PILLARS, team: TEAM, founders: FOUNDERS_COLL = [], reasons: REASONS, stats: STATS, history: HISTORY, home_sections, sobre_videos: sobreVideos = [], certifications = [], brazil_states_map = {}, international_presence = {}, pillar_icons = {} } = useContent();
  const SO = useLangKV("sobre") || {};
  const computedStates = useMemo(() => {
    const counts = {};
    for (const h of (HISTORY || [])) {
      const m = /\/([A-Z]{2})\s*$/.exec((h.city || "").trim());
      if (m) counts[m[1]] = (counts[m[1]] || 0) + 1;
    }
    const cfg = brazil_states_map.states || {};
    const out = {};
    for (const uf of Object.keys(counts)) {
      out[uf] = { active: true, count: counts[uf], color: cfg[uf]?.color || (counts[uf] >= 10 ? "#FF5E00" : "#D4FF00") };
    }
    for (const [uf, st] of Object.entries(cfg)) {
      if (st.active && !out[uf]) out[uf] = st;
    }
    return out;
  }, [HISTORY, brazil_states_map.states]);
  const { t } = useLang();
  // Founders: prefer the new dedicated /admin/founders CMS collection; fall back to the legacy home_sections.founders entry so existing data doesn't disappear.
  const FOUNDERS = FOUNDERS_COLL.length > 0 ? FOUNDERS_COLL : (home_sections?.founders || []);
  const [year, setYear] = useState("2026");
  const [kw, setKw] = useState("");

  const filtered = useMemo(() => HISTORY.filter((h) => {
    if (year !== "todos" && year !== "anterior" && String(h.year) !== year) return false;
    if (year === "anterior" && h.year >= 2020) return false;
    if (kw && !`${h.developer} ${h.project} ${h.city} ${h.type} ${(h.types || []).join(" ")}`.toLowerCase().includes(kw.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    // Newest year first, then alphabetical within each year (by project name, fallback to developer).
    if (a.year !== b.year) return (b.year || 0) - (a.year || 0);
    const an = String(a.project || a.developer || "").toLocaleLowerCase("pt-BR");
    const bn = String(b.project || b.developer || "").toLocaleLowerCase("pt-BR");
    return an.localeCompare(bn, "pt-BR", { sensitivity: "base" });
  }), [year, kw, HISTORY]);

  // Sum of cities and projects for the current filter — displayed as a discreet counter.
  const filterCount = useMemo(() => {
    const cities = new Set(filtered.map((h) => h.city).filter(Boolean));
    return { projects: filtered.length, cities: cities.size };
  }, [filtered]);

  const availableYears = useMemo(() => {
    // The CMS can override the exposed years via home_sections.history_visible_years (CSV list).
    const cmsList = (home_sections?.history_visible_years || "").split(",").map((s) => s.trim()).filter(Boolean);
    const ys = cmsList.length > 0 ? cmsList.map(Number).sort((a, b) => b - a) : [...new Set(HISTORY.map((h) => h.year))].sort((a, b) => b - a).filter((y) => y >= 2020);
    return ys;
  }, [HISTORY, home_sections]);

  // Row bg color: LANÇAMENTO → light green; SUSTENTAÇÃO → light yellow. Supports single `h.type`
  // (legacy) or a `h.types` list (new multi-select) — case-insensitive matching.
  const rowBgClass = (h) => {
    const types = (h.types && h.types.length ? h.types : (h.type ? [h.type] : [])).map((t) => String(t).toLowerCase());
    if (types.some((t) => t.includes("lançamento") || t.includes("lancamento"))) return "bg-green-50";
    if (types.some((t) => t.includes("sustenta"))) return "bg-yellow-50";
    return "";
  };
  const displayTypes = (h) => (h.types && h.types.length ? h.types.join(", ") : (h.type || ""));

  return (
    <>
      <SEO
        title={SO.seo_title || t.pages?.sobreTitle || "Sobre"}
        description={SO.seo_description || SO.manifesto_paragraph || "Places — três décadas construindo legado imobiliário com narrativa, método e IA. Google Partner."}
        focusKeyword={SO.seo_main_keyword || "agência de marketing imobiliário"}
        keywords={SO.seo_secondary_keywords || ["Places", "branding imobiliário", "legado imobiliário", "Google Partner"]}
        breadcrumbs={[
          { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
          { name: "Sobre", url: typeof window !== "undefined" ? window.location.href : "/sobre" },
        ]}
      />
      <section className="theme-light pt-36 pb-20 md:pt-44 md:pb-28 relative overflow-hidden grain">
        <AnimatedBlob className="bg-accent-green top-0 right-10 w-[400px] h-[400px] opacity-30" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
          <Reveal>
            <div className="text-xs uppercase tracking-widest mb-6 text-neutral-500">{SO.hero_eyebrow || "Sobre · The Real Estate Legacy"}</div>
            {/* HTML content is sanitized via DOMPurify with a strict allowlist (see SAFE_HTML_OPTS above). */}
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.9]" dangerouslySetInnerHTML={{ __html: sanitize(SO.hero_title_html || "Um legado <em class=\"not-italic text-accent-orange\">imobiliário</em>.") }} />
            <p className="mt-8 max-w-2xl text-lg text-neutral-700">{SO.hero_subtitle || "Construído por mais de três décadas ao lado das maiores incorporadoras do Brasil — com método próprio, time multidisciplinar e parceria oficial Google."}</p>
          </Reveal>
          <Reveal delay={0.15}>
            {SO.hero_video_url ? (
              <div className="mt-12"><VideoEmbed src={SO.hero_video_url} poster={SO.hero_video_poster} title="Places agency" aspect="aspect-[16/9]" /></div>
            ) : (
              <div className="mt-12 aspect-[16/9] bg-black relative overflow-hidden group cursor-pointer"><img src={SO.hero_video_poster || "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"} alt="Places agency" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition duration-700" /><div className="absolute inset-0 flex items-center justify-center"><div className="w-20 h-20 rounded-full bg-accent-yellow text-black flex items-center justify-center group-hover:scale-110 transition" data-testid="about-play"><Play className="w-7 h-7 ml-1" fill="currentColor" /></div></div></div>
            )}
          </Reveal>

          {/* 3 videos abaixo do principal — CMS editáveis, YouTube ou upload direto */}
          {sobreVideos.length > 0 && (
            <Reveal delay={0.25}>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sobre-videos">
                {sobreVideos.map((v, i) => (
                  <div key={`${v.title}-${i}`} data-testid={`sobre-video-${i}`}>
                    {v.url && (v.url.startsWith("/api/media/") || /youtube|youtu\.be|vimeo/i.test(v.url) || /\.(mp4|webm|mov)/i.test(v.url)) ? (
                      <div>
                        <VideoEmbed src={v.url} poster={v.poster} title={v.title} aspect="aspect-video" />
                        {v.title && <div className="mt-2 font-display text-base text-black">{v.title}</div>}
                      </div>
                    ) : (
                      <a href={v.url || "#"} target={v.url ? "_blank" : undefined} rel={v.url ? "noopener noreferrer" : undefined} className="group block relative aspect-video overflow-hidden bg-black">
                        <img src={v.poster} alt={v.title} loading="lazy" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-700" />
                        <div className="absolute inset-0 flex items-end p-4">
                          <div>
                            <div className="w-10 h-10 rounded-full bg-accent-yellow text-black flex items-center justify-center mb-2 group-hover:scale-110 transition"><Play className="w-4 h-4 ml-0.5" fill="currentColor" /></div>
                            <div className="font-display text-lg text-white drop-shadow-lg">{v.title}</div>
                          </div>
                        </div>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="theme-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          {/* Sanitized via DOMPurify (see SAFE_HTML_OPTS) */}
          <Reveal className="md:col-span-7"><h2 className="font-display text-4xl md:text-6xl leading-tight" dangerouslySetInnerHTML={{ __html: sanitize(SO.manifesto_title_html || "O imóvel na planta é, antes de tudo, <span class=\"text-accent-orange\">sonho</span>, projeção e percepção de valor.") }} /></Reveal>
          <Reveal delay={0.1} className="md:col-span-5 text-lg text-neutral-700 leading-relaxed space-y-4"><p>{SO.manifesto_paragraph || "Por isso, nosso método combina ciência e arte: dados de mercado para reduzir o risco, e narrativa de marca para gerar o desejo."}</p><div className="text-accent-orange font-semibold">{SO.manifesto_hashtag || "#ApaixonadosPorImobiliario"}</div></Reveal>
        </div>
      </section>

      {/* PILARES */}
      <section className="theme-dark py-24 md:py-32 grain relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><div className="text-xs uppercase tracking-widest text-neutral-500 mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(SO.pillars_eyebrow || "Nosso Método", { ALLOWED_TAGS: ["b","strong","em","i","u","span"], ALLOWED_ATTR: ["class","style"] }) }} /><h2 className="font-display text-4xl md:text-6xl mb-16 max-w-3xl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(SO.pillars_title || "Quatro pilares da Construção de Valor do Lugar.", { ALLOWED_TAGS: ["b","strong","em","i","u","span"], ALLOWED_ATTR: ["class","style"] }) }} /></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => {
              const ICONS = { compass: Compass, lightbulb: Lightbulb, target: Target, rocket: Rocket, award: Award, mapPin: MapPin };
              const iconKey = pillar_icons[String(p.n)] || ["compass", "lightbulb", "target", "rocket"][i] || "compass";
              const IconComp = ICONS[iconKey] || Compass;
              return (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="border border-white/10 p-8 hover:border-accent-yellow transition group h-full" data-testid={`pillar-${i}`}>
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-3">
                      <IconComp className="w-10 h-10" style={{ color: p.color }} strokeWidth={1.2} data-testid={`pillar-icon-${i}`} />
                      <span className="font-display text-5xl" style={{ color: p.color }}>{p.n}</span>
                    </div>
                    <div><div className="font-display text-2xl">{p.title}</div><div className="text-sm text-neutral-400 mt-1">{p.subtitle}</div><p className="mt-4 text-sm text-neutral-300 leading-relaxed">{p.text}</p></div>
                  </div>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* NUMBERS REPEAT */}
      <section className="theme-light py-20 md:py-28 border-y border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (<Reveal key={s.label} delay={i * 0.07}><div className="font-display text-5xl md:text-7xl"><CountUp value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} /></div><div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">{s.label}</div></Reveal>))}
        </div>
      </section>

      {/* TEAM */}
      <section className="theme-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><h2 className="font-display text-4xl md:text-6xl mb-12">{SO.team_title || "O time."}</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.05}>
                <div className="group" data-testid={`team-${i}`}>
                  <div className="aspect-square overflow-hidden bg-neutral-200 rounded-full"><img src={m.photo} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" /></div>
                  <div className="pt-4">
                    <div className="font-semibold text-sm md:text-base">{m.name}</div>
                    <div className="text-[11px] md:text-xs text-neutral-500">{m.role}</div>
                    <div className="text-[11px] md:text-xs text-neutral-600 mt-2 mb-3 leading-relaxed">{m.bio}</div>
                    {m.linkedin && (
                      <a href={m.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`team-linkedin-${i}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 border border-black/15 rounded-full hover:bg-[#0a66c2] hover:text-white hover:border-transparent transition">
                        <Linkedin className="w-3 h-3" /> LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-16 text-center">
              <div className="font-display text-3xl md:text-5xl lg:text-6xl leading-tight text-black" data-testid="team-footnote">
                {SO.team_footnote || "+22 colaboradores em Goiânia, Cuiabá e Brasília."}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}><div className="mt-16 border-t border-black/10 pt-10"><div className="text-xs uppercase tracking-widest text-neutral-500 mb-6">{SO.founders_title || "Fundadores"}</div><div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {FOUNDERS.map((f) => (
              <div key={f.name || f.id} data-testid={`founder-${f.name}`}>
                <div className="aspect-square overflow-hidden bg-neutral-200 rounded-full"><img src={f.photo} alt={f.name} loading="lazy" className="w-full h-full object-cover" /></div>
                <div className="pt-3 font-semibold text-sm md:text-base">{f.name}</div>
                <div className="text-[11px] md:text-xs text-neutral-500">{f.role}</div>
                {f.bio && <div className="text-[11px] md:text-xs text-neutral-600 mt-2 leading-relaxed">{f.bio}</div>}
                {f.linkedin && (
                  <a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 border border-black/15 rounded-full hover:bg-[#0a66c2] hover:text-white hover:border-transparent transition" data-testid={`founder-linkedin-${f.name}`}>
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </a>
                )}
              </div>
            ))}
          </div></div></Reveal>
        </div>
      </section>

      {/* REASONS */}
      <section className="theme-dark py-24 md:py-32 grain">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><h2 className="font-display text-4xl md:text-6xl mb-12">{SO.reasons_title || "Por que escolher a Places?"}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-x-8 gap-y-6">
            {REASONS.map((r, i) => (
              <Reveal key={r} delay={i * 0.03}><div className="flex gap-4 border-b border-white/10 pb-5"><span className="font-display text-3xl text-accent-yellow">{String(i+1).padStart(2,"0")}</span><div className="text-base">{r}</div></div></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HISTORY TABLE */}
      <section id="historico" className="theme-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><h2 className="font-display text-4xl md:text-6xl mb-3">{SO.history_title || "Histórico de projetos."}</h2><p className="text-neutral-600 mb-10 max-w-2xl">{SO.history_subtitle || "Mais de 300 lançamentos em 17 estados. Filtre por ano ou busque por palavra-chave."}</p></Reveal>
          <div className="flex flex-nowrap md:flex-wrap gap-1.5 md:gap-3 mb-6 overflow-x-auto md:overflow-visible -mx-6 md:mx-0 px-6 md:px-0 no-scrollbar items-center">
            {["todos", ...availableYears.map(String), "anterior"].map((y) => {
              const short = y === "todos" ? "Todos" : y === "anterior" ? "Ant." : String(y).slice(-2);
              const full = y === "todos" ? "Todos" : y === "anterior" ? "Anterior" : y;
              return (
                <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`} className={`shrink-0 text-[11px] md:text-xs px-2.5 md:px-4 py-1.5 md:py-2 rounded-full border transition ${year===y?"bg-black text-white border-black":"border-black/15 hover:bg-black/5"}`}>
                  <span className="md:hidden">{short}</span>
                  <span className="hidden md:inline">{full}</span>
                </button>
              );
            })}
            <div className="shrink-0 flex items-center gap-2 md:ml-auto border border-black/15 rounded-full px-2.5 md:px-3 py-1 md:py-1.5"><Search className="w-3.5 h-3.5 md:w-4 md:h-4 text-neutral-500" /><input data-testid="history-search" value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Buscar..." className="bg-transparent outline-none text-xs md:text-sm w-24 md:w-auto" /></div>
          </div>
          <div className="text-[11px] md:text-xs text-neutral-500 mb-3" data-testid="history-count">
            {filterCount.projects} projeto{filterCount.projects === 1 ? "" : "s"} · {filterCount.cities} cidade{filterCount.cities === 1 ? "" : "s"}
          </div>
          <div className="border border-black/10 overflow-hidden">
            <table className="w-full text-[11px] md:text-sm table-fixed" data-testid="history-table">
              <thead className="bg-black text-white text-[9px] md:text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-1.5 md:p-4 w-[42px] md:w-auto">Ano</th>
                  <th className="text-left p-1.5 md:p-4">Empresa</th>
                  <th className="text-left p-1.5 md:p-4">Empreend.</th>
                  <th className="text-left p-1.5 md:p-4 hidden sm:table-cell">Cidade</th>
                  <th className="text-left p-1.5 md:p-4 hidden md:table-cell">Tipo</th>
                </tr>
              </thead>
              <tbody>{filtered.map((h) => (
                <tr key={`${h.year}-${h.project}`} className={`border-t border-black/5 hover:bg-black/[0.02] align-top ${rowBgClass(h)}`}>
                  <td className="p-1.5 md:p-4 font-semibold whitespace-nowrap"><span className="md:hidden">{String(h.year).slice(-2)}</span><span className="hidden md:inline">{h.year}</span></td>
                  <td className="p-1.5 md:p-4 break-words">{h.developer_url ? <a href={h.developer_url} target="_blank" rel="noopener noreferrer" className="link-underline" data-testid={`history-dev-link-${h.year}`}>{h.developer}</a> : h.developer}</td>
                  <td className="p-1.5 md:p-4 break-words">{h.project}</td>
                  <td className="p-1.5 md:p-4 text-neutral-600 hidden sm:table-cell break-words">{h.city}</td>
                  <td className="p-1.5 md:p-4 text-neutral-600 hidden md:table-cell break-words">{displayTypes(h)}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-neutral-500">Nenhum projeto encontrado.</td></tr>}</tbody>
            </table>
          </div>
          <div className="mt-8"><Link to="/cases" className="btn-sharp bg-black text-white rounded-full hover:bg-accent-yellow hover:text-black" data-testid="history-see-cases">Ver cases →</Link></div>
        </div>
      </section>

      {/* BRAZIL MAP + INTERNATIONAL PRESENCE */}
      <section className="theme-dark py-24 md:py-32 grain relative overflow-hidden" data-testid="section-presence">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Brazil Radar Map */}
            <Reveal>
              <div className="text-xs uppercase tracking-widest text-accent-orange mb-3">Presença Nacional</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight mb-2">{brazil_states_map.title || "Onde já estivemos no Brasil"}</h2>
              <p className="text-neutral-400 mb-8 max-w-md">{brazil_states_map.subtitle || "Mais de 300 lançamentos em 17 estados."}</p>
              <BrazilRadarMap states={computedStates} />
            </Reveal>

            {/* International */}
            <Reveal delay={0.2}>
              <div className="text-xs uppercase tracking-widest text-accent-orange mb-3">Atuação Internacional</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight mb-2">{international_presence.title || "Além das fronteiras."}</h2>
              <p className="text-neutral-400 mb-8 max-w-md">{international_presence.subtitle || "Projetos que atravessam continentes."}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="intl-grid">
                {((international_presence.countries && international_presence.countries.length > 0) ? international_presence.countries : [
                  { name: "Portugal", iso: "PT", projects: 3, products: ["Vale do Lima Residências", "Porto Living", "Ericeira Vista"] },
                  { name: "Argentina", iso: "AR", projects: 2, products: ["Puerto Madero Riverside", "Palermo Urban"] },
                  { name: "Paraguai", iso: "PY", projects: 1, products: ["Asunción Prime Tower"] },
                  { name: "Uruguai", iso: "UY", projects: 1, products: ["Punta del Este Ocean View"] },
                ]).map((c, i) => (
                  <ExpandableCountryCard key={c.iso || i} country={c} index={i} />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECONHECIMENTOS · PARCERIAS · CERTIFICAÇÕES (3 seções) */}
      {certifications.length > 0 && (() => {
        const groups = [
          { key: "reconhecimento", title: t.sections?.recognitionsTitle || "Reconhecimentos", subtitle: t.sections?.recognitionsSubtitle || "Prêmios e destaques recebidos.", items: certifications.filter((c) => (c.category || "certificacao") === "reconhecimento") },
          { key: "parceria", title: t.sections?.partnershipsTitle || "Parcerias Oficiais", subtitle: t.sections?.partnershipsSubtitle || "Marcas com quem operamos de forma oficial.", items: certifications.filter((c) => c.category === "parceria") },
          { key: "certificacao", title: t.sections?.certificationsTitle || "Certificações", subtitle: t.sections?.certificationsSubtitle || "Selos que atestam nosso padrão de qualidade.", items: certifications.filter((c) => (c.category || "certificacao") === "certificacao") },
        ].filter((g) => g.items.length > 0);
        return (
          <section className="theme-light py-20 md:py-28 border-t border-black/10 relative overflow-hidden" data-testid="section-recognitions">
            <AnimatedBlob className="bg-accent-yellow -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] opacity-20" />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative space-y-16">
              {groups.map((g) => (
                <div key={g.key} data-testid={`section-${g.key}`}>
                  <Reveal>
                    <div className="text-xs uppercase tracking-widest text-accent-orange mb-3 font-bold">{g.title}</div>
                    <h2 className="font-display text-2xl md:text-4xl mb-10 leading-tight max-w-3xl">{g.subtitle}</h2>
                  </Reveal>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {g.items.map((cert, i) => {
                      const inner = (
                        <div className="border border-black/10 p-6 md:p-7 flex flex-col items-center justify-between h-full hover:border-accent-orange hover:shadow-lg transition group bg-white" data-testid={`${g.key}-cert-${i}`}>
                          <div className="flex-1 flex items-center justify-center min-h-[80px] md:min-h-[100px] w-full">
                            {cert.logo ? (
                              <img src={cert.logo.startsWith("http") ? cert.logo : `${process.env.REACT_APP_BACKEND_URL}${cert.logo}`} alt={cert.name} loading="lazy" className="max-h-14 md:max-h-16 max-w-full object-contain group-hover:scale-105 transition-transform" />
                            ) : (
                              <span className="font-display text-xl text-neutral-400">{cert.name}</span>
                            )}
                          </div>
                          <div className="mt-4 text-center">
                            <div className="font-semibold text-sm">{cert.name}</div>
                            {cert.description && <div className="text-xs text-neutral-500 mt-1">{cert.description}</div>}
                          </div>
                        </div>
                      );
                      return cert.url ? (
                        <a key={`${g.key}-${cert.name}-${i}`} href={cert.url} target="_blank" rel="noopener noreferrer" className="block">{inner}</a>
                      ) : (
                        <div key={`${g.key}-${cert.name}-${i}`}>{inner}</div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}
    </>
  );
}

function ExpandableCountryCard({ country: c, index }) {
  const [open, setOpen] = useState(false);
  const products = Array.isArray(c.products) ? c.products.filter(Boolean) : [];
  const hasProducts = products.length > 0;
  return (
    <div className="relative border-2 border-white/15 rounded-lg overflow-hidden hover:border-accent-orange transition-colors group" data-testid={`intl-country-${c.iso || index}`}>
      <button
        onClick={() => hasProducts && setOpen((s) => !s)}
        className="w-full text-left p-4 relative"
        disabled={!hasProducts}
        aria-expanded={open}
        data-testid={`intl-country-toggle-${c.iso || index}`}
      >
        <div className="absolute top-3 right-3 text-2xl leading-none opacity-90">{isoToFlag(c.iso)}</div>
        <div className="font-display text-xl mt-8 pr-8">{c.name}</div>
        <div className="text-[11px] text-neutral-500 mt-1">{c.projects || products.length || 0} projeto(s)</div>
        <div className="flex justify-between items-center mt-2">
          <div className="text-[10px] font-mono text-neutral-500 opacity-60">{c.iso}</div>
          {hasProducts && (
            <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }} className="text-accent-orange">
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          )}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && hasProducts && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="p-4 pt-3 bg-white/[0.03]">
              <div className="text-[10px] uppercase tracking-widest text-accent-orange mb-2 font-bold">Projetos</div>
              <ul className="space-y-1.5">
                {products.map((p, i) => (
                  <li key={i} className="text-xs text-neutral-300 flex items-start gap-2" data-testid={`intl-country-product-${c.iso || index}-${i}`}>
                    <span className="text-accent-yellow mt-1">▸</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
